/**
 * AIQuestionChip — floating bottom-right surface that lets the AI
 * agent ask the human clarifying questions during a match (e.g.
 * "did you mean to draw 2 instead of 1?", "are you conceding the
 * stone here?").
 *
 * Replaces the old CoachPanel's live-commentary surface (removed
 * earlier this iteration per user direction). This component owns
 * only the UI; the question source is currently local-only — a later
 * pass wires it to the coach agent / judge worker once those expose a
 * push channel.
 *
 * For testing during the closed-beta sandbox phase a hidden
 * "(test)" button injects a synthetic question so testers can verify
 * the popup flow without waiting for a real agent message.
 */

import { Bot } from 'lucide-react';
import { type ReactElement, useState } from 'react';

type AIQuestion = {
  id: string;
  text: string;
  asked_at: number;
};

export function AIQuestionChip(): ReactElement {
  const [questions, setQuestions] = useState<readonly AIQuestion[]>([]);
  const [open, setOpen] = useState(false);
  const [reply, setReply] = useState('');
  const [showTest, setShowTest] = useState(false);

  const dismiss = (id: string): void => {
    setQuestions((q) => q.filter((x) => x.id !== id));
    setReply('');
  };

  const injectTest = (): void => {
    setQuestions((q) => [
      ...q,
      {
        id: `q-${Date.now()}`,
        text: 'I noticed you tapped Aurelia without paying its activation cost — was that a Free-Tap activation?',
        asked_at: Date.now(),
      },
    ]);
    setOpen(true);
  };

  const active = questions[0];
  const count = questions.length;

  return (
    <div className="fixed right-3 bottom-24 z-40 flex flex-col items-end gap-2">
      {open && active && (
        <div className="w-80 rounded-md border border-violet-700 bg-cosmos-900 p-3 shadow-2xl">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-mono text-[10px] text-violet-300 uppercase tracking-wide">
              AI question
            </span>
            <button
              type="button"
              className="text-cosmos-400 text-xs hover:text-cosmos-200"
              onClick={() => setOpen(false)}
            >
              ✕
            </button>
          </div>
          <p className="mb-3 text-cosmos-100 text-sm">{active.text}</p>
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Reply (optional) — Esc to cancel, Enter to send"
            className="mb-2 w-full rounded border border-cosmos-700 bg-cosmos-950 p-2 text-cosmos-100 text-xs"
            rows={3}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setOpen(false);
                setReply('');
              } else if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                dismiss(active.id);
              }
            }}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-cosmos-700 px-3 py-1 text-xs hover:bg-cosmos-800"
              onClick={() => dismiss(active.id)}
            >
              Dismiss
            </button>
            <button
              type="button"
              className="rounded bg-violet-700 px-3 py-1 font-medium text-white text-xs hover:bg-violet-600"
              onClick={() => dismiss(active.id)}
            >
              Send reply
            </button>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          if (count === 0) {
            // Surface the test injector when there are no real
            // questions yet — useful for testers to verify the flow.
            setShowTest((s) => !s);
            return;
          }
          setOpen((o) => !o);
        }}
        className={`flex items-center gap-2 rounded-full border-2 px-3 py-1.5 font-mono text-[11px] shadow-lg transition ${
          count > 0
            ? 'border-violet-500 bg-violet-700 text-white animate-pulse hover:animate-none'
            : 'border-cosmos-700 bg-cosmos-800 text-cosmos-400 hover:border-cosmos-500'
        }`}
        title={count > 0 ? 'AI has a question' : 'No AI questions'}
      >
        <Bot className="h-3.5 w-3.5" aria-hidden="true" />
        <span>{count > 0 ? `AI question (${count})` : 'AI quiet'}</span>
      </button>
      {showTest && count === 0 && (
        <button
          type="button"
          onClick={injectTest}
          className="rounded border border-dashed border-violet-700 bg-cosmos-900 px-3 py-1 text-[10px] text-violet-300 hover:bg-cosmos-800"
        >
          (test) inject question
        </button>
      )}
    </div>
  );
}
